import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, ArrowRight } from 'lucide-react';

const Pricing = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#854DFF] to-[#7A42E6] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Enterprise Pricing
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Custom solutions tailored to your business needs. Let's discuss how CredFlexi can transform your operations.
            </p>
          </div>
        </div>
      </section>

      {/* Contact for Pricing Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-2xl p-12 border border-gray-100">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Contact Us for Pricing
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Every enterprise has unique requirements. Our team will work with you to create a customized solution and pricing plan that fits your specific needs, scale, and budget.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-gradient-to-br from-[#854DFF]/5 to-[#854DFF]/10 p-8 rounded-xl border border-[#854DFF]/20">
                <Mail className="w-12 h-12 text-[#854DFF] mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Email Us</h3>
                <p className="text-gray-600 mb-4">
                  Send us your requirements and we'll get back to you within 24 hours.
                </p>
                <a
                  href="mailto:info@credflexi.com"
                  className="text-[#854DFF] font-semibold hover:underline inline-flex items-center"
                >
                  info@credflexi.com
                  <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </div>

              <div className="bg-gradient-to-br from-[#854DFF]/5 to-[#854DFF]/10 p-8 rounded-xl border border-[#854DFF]/20">
                <Phone className="w-12 h-12 text-[#854DFF] mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Schedule a Call</h3>
                <p className="text-gray-600 mb-4">
                  Talk to our sales team to discuss your specific requirements and get a custom quote.
                </p>
                <Link
                  to="/contact"
                  className="text-[#854DFF] font-semibold hover:underline inline-flex items-center"
                >
                  Schedule Enterprise Demo
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="text-center">
              <Link
                to="/contact"
                className="inline-flex items-center bg-gradient-to-r from-[#854DFF] to-[#7A42E6] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:from-[#7A42E6] hover:to-[#6F37CC] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Get Custom Pricing
                <ArrowRight className="ml-3 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Custom Pricing Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Custom Pricing?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We believe in providing value tailored to your specific use case
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-[#854DFF]/10 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Tailored Solutions
              </h3>
              <p className="text-gray-600">
                Every enterprise has unique requirements. We create solutions and pricing that match your specific business needs and scale.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-[#854DFF]/10 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">💰</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Flexible Terms
              </h3>
              <p className="text-gray-600">
                Choose payment terms and commitment levels that work for your organization, from monthly to multi-year contracts.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-[#854DFF]/10 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">📈</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Scale with You
              </h3>
              <p className="text-gray-600">
                Pricing that grows with your business. Start where you are and scale up as your needs expand.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-r from-[#854DFF] to-[#7A42E6] rounded-2xl p-12 text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Let's discuss how CredFlexi can help transform your enterprise commerce operations.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center bg-white text-[#854DFF] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Contact Sales Team
              <ArrowRight className="ml-3 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
